// AlexStaff (B-693): запоминание и восстановление положения прокрутки страницы при возврате крестиком карточки.
// Любой сбой — тихо ничего не делаем (поведение как до патча).
import { type Location } from './types'

// запись стека возврата: адрес + положение прокрутки основного Scroller'а страницы (необязательное)
export interface PanelEntry {
  loc: Location
  scroll?: number
}

const MAX_TRACKED = 10
const RESTORE_LIMIT_MS = 10000
const RESTORE_STEP_MS = 100
// Держим позицию 2,5 с подряд: документ после первой отрисовки перерисовывается ещё раз (догрузка совместного
// редактора) и сбрасывает прокрутку наверх — проверка Алексея 03.10 «сначала на то же место, а потом наверх».
const SETTLE_TICKS = 25

export function makeEntry (loc: Location, scroll: number | undefined): PanelEntry {
  return typeof scroll === 'number' && Number.isFinite(scroll) && scroll > 0 ? { loc, scroll } : { loc }
}

// читает запись из sessionStorage: новый формат {loc, scroll?} и старый (голый Location с path)
export function readEntry (raw: any): PanelEntry | undefined {
  if (raw === null || typeof raw !== 'object') return undefined
  if (Array.isArray(raw.path)) return { loc: raw as Location }
  if (raw.loc !== null && typeof raw.loc === 'object' && Array.isArray(raw.loc.path)) {
    return makeEntry(raw.loc as Location, raw.scroll)
  }
  return undefined
}

// основной прокручиваемый блок — самый большой по площади из Scroller'ов (.scroll)
export function pickLargest<T extends { clientWidth: number, clientHeight: number }> (els: T[]): T | undefined {
  let best: T | undefined
  let bestArea = -1
  for (const el of els) {
    const area = el.clientWidth * el.clientHeight
    if (area > bestArea) {
      best = el
      bestArea = area
    }
  }
  return best
}

function scrollers (): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>('.scroll'))
}

const tops = new Map<string, number>()
let tracking = false

// следим за прокруткой (событие scroll не всплывает — ловим в capture); данные нужны к моменту ухода со страницы,
// когда сама страница уже может быть размонтирована
export function trackScroll (getUrl: () => string | undefined): void {
  if (tracking || typeof document === 'undefined') return
  tracking = true
  try {
    document.addEventListener(
      'scroll',
      (ev) => {
        try {
          const el = ev.target
          if (!(el instanceof HTMLElement) || !el.classList.contains('scroll')) return
          if (pickLargest(scrollers()) !== el) return
          const url = getUrl()
          if (url === undefined) return
          tops.delete(url)
          tops.set(url, el.scrollTop)
          if (tops.size > MAX_TRACKED) tops.delete(tops.keys().next().value as string)
        } catch {}
      },
      true
    )
  } catch {}
}

export function trackedScroll (url: string): number | undefined {
  return tops.get(url)
}

let cancelRestore: (() => void) | undefined

// возвращает прокрутку на top, дожидаясь, пока содержимое отрисуется; прекращает при ручной прокрутке пользователя
export function restoreScroll (top: number | undefined): void {
  cancelRestore?.()
  cancelRestore = undefined
  if (top === undefined || top <= 0 || typeof document === 'undefined') return
  try {
    const started = Date.now()
    let settled = 0
    let timer: ReturnType<typeof setTimeout> | undefined
    const events = ['wheel', 'touchstart', 'keydown', 'mousedown']
    const stop = (): void => {
      if (timer !== undefined) clearTimeout(timer)
      events.forEach((e) => {
        document.removeEventListener(e, stop, true)
      })
      if (cancelRestore === stop) cancelRestore = undefined
    }
    events.forEach((e) => {
      document.addEventListener(e, stop, true)
    })
    cancelRestore = stop
    const tick = (): void => {
      try {
        const el = pickLargest(scrollers())
        if (el !== undefined && el.scrollHeight - el.clientHeight >= top) {
          if (Math.abs(el.scrollTop - top) > 1) {
            el.scrollTop = top
            settled = 0
          } else settled++
          if (settled >= SETTLE_TICKS) {
            stop()
            return
          }
        }
        if (Date.now() - started > RESTORE_LIMIT_MS) {
          stop()
          return
        }
        timer = setTimeout(tick, RESTORE_STEP_MS)
      } catch {
        stop()
      }
    }
    timer = setTimeout(tick, RESTORE_STEP_MS)
  } catch {}
}
